import type {
	FilesResponseType,
	OneDriveFileType,
	OneDrivePostMessageType,
} from './OneDriveTypes.ts';
import { OneDriveActionTypes, OneDriveErrorStatuses } from './OneDriveTypes.ts';

/**
 * The class is a wrapper over OneDrive Picker:
 * https://docs.microsoft.com/en-us/onedrive/developer/controls/file-pickers/js-v72/?view=odsp-graph-online
 * */

export type OnIframeClickFn = (pickerPromise: Promise<FilesResponseType | null>) => void;

export type OneDrivePickerOptionsType = {
	multiSelect: boolean;
	iframe: HTMLIFrameElement;
	onIframeClick: OnIframeClickFn;
	// Client ID based on environment, e.g. commercial or fedRAMP
	// TODO: This is optional for FG check, it can be made required later.
	microsoftClientId?: string;
};

export class OneDrivePicker {
	private options: OneDrivePickerOptionsType;
	private pickerPromise: any = {};

	constructor(opt: OneDrivePickerOptionsType) {
		this.options = opt;
	}

	init(): this {
		this.options.iframe.contentWindow?.postMessage(
			{
				type: 'ifs-onedrive',
				actionType: OneDriveActionTypes.INIT,
				pickerOptions: {
					multiSelect: this.options.multiSelect,
				},
				microsoftClientId: this.options.microsoftClientId,
			},
			'*',
		);

		window.addEventListener('message', this.pickerListener, false);
		return this;
	}

	unmount(): void {
		this.options.iframe.contentWindow?.postMessage(
			{
				type: 'ifs-onedrive',
				actionType: OneDriveActionTypes.UNMOUNT,
			},
			'*',
		);
		window.removeEventListener('message', this.pickerListener, false);
	}

	private handleIframeClick = () => {
		this.pickerPromise.promise = new Promise((resolve, reject) => {
			this.pickerPromise.resolve = resolve;
			this.pickerPromise.reject = reject;
		});
		this.options.onIframeClick(this.pickerPromise.promise);
	};

	private handlePickerSuccess = (pickerData) => {
		//there's no value if the file is removed or the access is disabled
		// https://hello.atlassian.net/wiki/spaces/CCECO/pages/1277983936/IFS+office+365+Use+cases#10.-Should-show-the-error-if-a-document-is-not-shared-anymore.
		pickerData.value
			? this.pickerPromise.resolve(pickerData.value)
			: this.pickerPromise.reject({
					status: OneDriveErrorStatuses.NO_ACCESS,
				});
	};

	private pickerListener = (event: OneDrivePostMessageType) => {
		if (event.data?.type !== 'ifs-onedrive') return;

		const { actionType, pickerData } = event.data || {};

		switch (actionType) {
			case OneDriveActionTypes.CLICK: {
				this.handleIframeClick();
				break;
			}

			case OneDriveActionTypes.SUCCESS: {
				this.handlePickerSuccess(pickerData);
				break;
			}

			case OneDriveActionTypes.CANCEL: {
				this.pickerPromise.resolve(null);
				break;
			}

			case OneDriveActionTypes.ERROR: {
				this.pickerPromise.reject(pickerData);
				break;
			}
		}
	};

	async exportFromMicrosoftGraph(selectedDoc: OneDriveFileType): Promise<File> {
		//due to CORS error cfetch can't be used because it adds the credentials field
		/* oxlint-disable-next-line eslint-core/no-restricted-syntax */ // eslint-disable-next-line check-credentials-option/has-credentials-option, no-restricted-syntax
		const response = await fetch(selectedDoc['@microsoft.graph.downloadUrl']);

		if (!response.ok) {
			throw {
				status: response.status,
			};
		}

		const fileBlob = await response.blob();
		return new File([fileBlob], selectedDoc.name, {
			type: fileBlob.type,
		});
	}
}
